import { useCallback, useContext, useMemo, useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import notDeleted from '../../helpers/notDeleted'
import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { useFirestoreGeneralOrderBy } from '../../firebase/useFirestore'
import crossTabScaleAverages from '../../helpers/analysisHelpers/crossTabScaleAverages'
import DeepAnalysisSectionHeader from './DeepAnalysisSectionHeader'
import DeepAnalysisPeriodContext from '../../StateManagment/DeepAnalysisPeriodContext'
import ComparisonFrame from '../dataVisualizations/ComparisonFrame'
import ChartDataTable from '../graphData/ChartDataTable'
import { wideTable } from '../../helpers/dataHelpers/chartDataTable'
import filterSubmissionsByPeriod from '../../helpers/compareHelpers/filterSubmissionsByPeriod'
import seriesColorMap from '../../helpers/compareHelpers/seriesColorMap'
import sharedAxisMax, { axisTicks } from '../../helpers/compareHelpers/sharedAxisMax'
import { CATEGORICAL } from '../../helpers/chartPalette'
import { CHART_MARK_STROKE, CHART_MARK_STROKE_WIDTH } from '../../helpers/chartPalette'

const FILTER_TYPES = ['multiple-one', 'multiple-multiple']
// Brand palette, cycled across the filter-option bar series.
const SERIES_COLORS = CATEGORICAL

/**
 * One period's cross-tab chart, or why there is nothing to show.
 *
 * @param {object} props
 * @param {{chartData: object[], filterOptions: string[]}} props.result - Cross-tab for this period
 * @param {boolean} props.hasPeriod - The result covers a chosen period (changes the empty wording)
 * @param {Map<string, string>} props.colors - Colour per answer option, shared by both compared periods
 * @param {number} [props.yMax] - Top of the value axis, shared by both compared periods
 */
const FilterPanel = ({ result, hasPeriod, colors, yMax }) => {
    if (result.chartData.length === 0) {
        return (
            <p className='deep-analysis-empty'>
                {hasPeriod
                    ? 'Geen schaalvragen met antwoorden in deze periode om uit te splitsen.'
                    : 'Geen schaalvragen met antwoorden om uit te splitsen.'}
            </p>
        )
    }
    return (
        <div className='deep-analysis-compare-chart'>
            <ResponsiveContainer width='100%' height={300}>
                <BarChart data={result.chartData} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='name' />
                    <YAxis domain={yMax ? [0, yMax] : undefined} ticks={axisTicks(yMax)} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    {result.filterOptions.map((option) => (
                        <Bar
                            key={option}
                            dataKey={option}
                            fill={colors.get(option)}
                        stroke={CHART_MARK_STROKE} strokeWidth={CHART_MARK_STROKE_WIDTH} />
                    ))}
                </BarChart>
            </ResponsiveContainer>
            {/* Het gemiddelde per gekozen antwoord, voor de kopie. */}
            <ChartDataTable table={wideTable(result.chartData, result.filterOptions, 'Vraag')} />
        </div>
    )
}

/**
 * "Resultaten specificeren" section on a research deep-analysis page.
 *
 * The user picks one multiple-choice question from the anchor research; the
 * section cross-tabs every scale question's average score per chosen option
 * (respondents grouped per submission by their millisecond Timestamp — one
 * submission shares one Timestamp). Display-only — ports the old FilterResults
 * builder without its save-to-Insights behavior.
 *
 * Follows the page period (DeepAnalysisPeriodContext): the research is read
 * once, then cross-tabbed per period over the whole submissions inside it
 * (filterSubmissionsByPeriod) — one chart per period when comparing.
 * AI-CONTEXT: See docs/features/deep-analysis-period.md
 *
 * @param {object} props
 * @param {object} props.anchor - The anchor Research doc
 */
const ResultFilterSection = ({ anchor }) => {
    const [filterFieldId, setFilterFieldId] = useState('')
    // The research's answers for the filter that was run (null = not run yet).
    const [answers, setAnswers] = useState(/** @type {object[] | null} */ (null))
    const [ranFieldId, setRanFieldId] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const period = useContext(DeepAnalysisPeriodContext)
    const compare = Boolean(period?.compareActive)

    const fields = useFirestoreGeneralOrderBy(
        'QuestionnaireFields',
        'QuestionnaireID',
        anchor.QuestionnaireID || '',
        'Position',
        'asc'
    )
    const fieldList = Array.isArray(fields) ? fields : []
    const filterQuestions = fieldList.filter((f) => FILTER_TYPES.includes(f.Type))
    const scaleFieldsById = new Map(
        fieldList.filter((f) => f.Type === 'scale').map((f) => [f.ID, f])
    )

    const runFilter = async () => {
        setLoading(true)
        setError('')
        setAnswers(null)

        try {
            // One read of the whole research, then group answers per submission
            // in memory (one submission shares one millisecond Timestamp). This
            // avoids an N+1 query — a submission fetch per respondent.
            const snap = await db.collection('QuestionnairesResponses')
                .where('CompagnyID', '==', client)
                .where('ResearchID', '==', anchor.ID)
                .get()

            // Deze read gaat buiten de hooks om, dus het centrale leesfilter
            // komt er niet aan te pas: verwijderde antwoorden zelf weglaten.
            // AI-CONTEXT: See docs/decisions.md (ADR-008)
            const kept = []
            snap.docs.filter((doc) => notDeleted(doc.data())).forEach((doc) => kept.push(doc.data()))
            setAnswers(kept)
            setRanFieldId(filterFieldId)
        } catch (err) {
            console.error('ResultFilterSection — filter failed:', err)
            setError('De resultaten konden niet worden geladen.')
        } finally {
            setLoading(false)
        }
    }

    // Cross-tab of the submissions inside one period (null range = all).
    const splitFor = useCallback((range) => {
        const submissions = new Map()
        filterSubmissionsByPeriod(answers || [], range).forEach((data) => {
            const ts = data.Timestamp
            const key = ts && typeof ts.toMillis === 'function' ? ts.toMillis() : String(ts)
            if (!submissions.has(key)) submissions.set(key, [])
            submissions.get(key).push(data)
        })

        // For each submission, pair the chosen filter option(s) with every
        // scale answer in that same submission. (multiple-multiple can carry
        // more than one filter answer per submission — each is an option.)
        const pairs = []
        submissions.forEach((group) => {
            const filterAnswers = group.filter((a) => a.FieldID === ranFieldId)
            if (filterAnswers.length === 0) return
            const scaleAnswers = group.filter((a) => scaleFieldsById.has(a.FieldID))
            filterAnswers.forEach((fa) => {
                scaleAnswers.forEach((sa) => {
                    const scaleField = scaleFieldsById.get(sa.FieldID)
                    pairs.push({
                        filterOption: fa.Input,
                        question: scaleField.Question,
                        value: sa.Input,
                        position: scaleField.Position || 0,
                    })
                })
            })
        })

        return crossTabScaleAverages(pairs)
        // scaleFieldsById is rebuilt from `fields` every render; key on fields.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers, ranFieldId, fields])

    // One colour per answer and one axis across both periods, or the two
    // halves can't be read side by side (testronde 2026-10-28, B1/B2).
    // AI-CONTEXT: See docs/features/deep-analysis-period.md
    const rangeA = period?.rangeA || null
    const rangeB = period?.rangeB || null
    const results = useMemo(
        () => (compare ? [rangeA, rangeB] : [rangeA]).map((range) => splitFor(range)),
        [splitFor, compare, rangeA, rangeB]
    )
    const colors = seriesColorMap(results.map((r) => r.filterOptions), SERIES_COLORS)
    const yMax = compare
        ? sharedAxisMax(results.flatMap((r) => r.chartData.flatMap((row) =>
            Object.entries(row).filter(([key]) => key !== 'name' && key !== 'position').map(([, value]) => value))))
        : undefined
    const panel = (range, side) => (
        <FilterPanel result={results[side === 'B' ? 1 : 0]} hasPeriod={Boolean(range)} colors={colors} yMax={yMax} />
    )

    return (
        <div className='deep-analysis-section'>
            <DeepAnalysisSectionHeader
                kicker='Specificeren'
                title='Resultaten specificeren'
                description={`Splits de gemiddelde scores op de schaalvragen van "${anchor.Title}" uit naar het antwoord op een meerkeuzevraag.`}
            />
            {filterQuestions.length === 0 ? (
                <p className='deep-analysis-empty'>
                    Dit onderzoek heeft geen meerkeuzevraag om op te filteren.
                </p>
            ) : (
                <>
                    <select
                        className='deep-analysis-indicator-select'
                        value={filterFieldId}
                        onChange={(e) => setFilterFieldId(e.target.value)}
                    >
                        <option value=''>-- Selecteer meerkeuzevraag --</option>
                        {filterQuestions.map((q) => (
                            <option key={q.ID} value={q.ID}>{q.Question}</option>
                        ))}
                    </select>
                    {filterFieldId && (
                        <button
                            className='deep-analysis-filter-button'
                            onClick={runFilter}
                            disabled={loading}
                        >
                            {loading ? 'Bezig…' : 'Filter'}
                        </button>
                    )}
                </>
            )}
            {error && <p className='deep-analysis-error'>{error}</p>}
            {answers && !loading && (compare
                ? <ComparisonFrame rangeA={period.rangeA} rangeB={period.rangeB} renderViz={panel} />
                : panel(period?.rangeA || null, 'A'))}
        </div>
    )
}

export default ResultFilterSection
